import { ReactElement, useMemo } from 'react';

import {
  CardDataSchema,
  Directions,
  InputTypes,
  PaymentFormField,
} from '@cashier/cashier-model';
import { cn } from '@cashier/cashier-ui';

import { useCashierConfiguration } from '../../hooks';
import {
  LeaderField,
  MaskedField,
  StandardField,
} from './modules/cashierField';
import {
  SecuredFormStateProvider,
  useSecuredFormStateContext,
} from './contexts';

import styles from './styles.module.scss';

interface CardDataProps {
  schema: CardDataSchema;
}

export const SecuredCardData = ({ schema }: CardDataProps): ReactElement => {
  const hasBothExpiryAndCvv = !!schema.expirationDate && !!schema.cvv;

  const { direction } = useCashierConfiguration();

  const { isFormReady } = useSecuredFormStateContext();

  const initialPayload = useMemo(() => {
    return Object.fromEntries(
      Object.values(schema)
        .filter((field: PaymentFormField | undefined) => {
          return field && field.type !== InputTypes.Checkbox;
        })
        .map((field: PaymentFormField) => [
          field.name,
          {
            isFocused: false,
            isFilled: false,
            isReady: false,
          },
        ]),
    ) as Record<
      string,
      {
        isReady: boolean;
        isFocused: boolean;
        isFilled: boolean;
      }
    >;
  }, [schema]);

  return (
    <SecuredFormStateProvider initialPayload={initialPayload}>
      <div>
        {schema.cardHolder && (
          <StandardField field={schema.cardHolder} direction={direction} />
        )}

        {schema.cardNumber && (
          <StandardField field={schema.cardNumber} direction={direction} />
        )}

        <div className={cn([styles.row, styles.withoutMargin])}>
          {schema.expirationDate && (
            <div
              className={cn({
                [styles.fieldHalf]: hasBothExpiryAndCvv,
                [styles.fieldFull]: !hasBothExpiryAndCvv,
                [styles.paddingRight]: hasBothExpiryAndCvv,
                [styles.rtl]: direction === Directions.Rtl,
              })}
            >
              <MaskedField
                direction={direction}
                field={schema.expirationDate}
              />
            </div>
          )}

          {schema.cvv && (
            <div
              className={cn({
                [styles.fieldHalf]: hasBothExpiryAndCvv,
                [styles.fieldFull]: !hasBothExpiryAndCvv,
              })}
            >
              <StandardField field={schema.cvv} direction={direction} />
            </div>
          )}
        </div>

        {isFormReady && (
          <LeaderField title="Leader Field" loading="lazy" schema={schema} />
        )}
      </div>
    </SecuredFormStateProvider>
  );
};
